import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { isDimension, isMeasure } from "../../../../../utilities/fieldsHelper.ts";
import type { ShowFieldOptionsSettings } from "../../../../viewer/common/Types.ts";
import DropFieldContainer from "../../../common/fields/DropFieldContainer.tsx";
import { createRowField } from "../../../common/utilities/createFields.ts";
import { extractMeta } from "../../../common/utilities/dropFieldContainerHelper.ts";
import { formatDimensionFieldCaption } from "../../../utils/formatDimensionFieldCaptions.tsx";
import RowFieldOptionPopup from "./RowFieldOptionPopup.tsx";

import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  ReportField,
} from "../../../../../../../../../api/types/svTypes.ts";
import { AreaItemType } from "../../../../../../../../../api/types/svTypes.ts";
import { selectColumns, selectConditions, selectRows } from "../../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import { getDimensionFields } from "../../../../../utilities/isConfigurationValid.ts";
import OtherOptionsWrapper from "../../../common/fields/field-options/OtherOptionsWrapper.tsx";
import SuppressBlankValues from "../../../common/fields/field-options/SuppressBlankValues.tsx";
import FieldOptionPopupContainer from "../../../common/fields/measures/FieldOptionPopupContainer.tsx";
import MeasureFieldOptions from "../../../common/fields/measures/MeasureFieldOptions.tsx";
import { canDropRowItem, MaxRowsNumber } from "./canDropRowItem.ts";

interface Props {
  readonlyMode: boolean;
}

export default function RowsDropFieldsContainer({ readonlyMode }: Props) {
  const conditionsAreaValues = useSelector(selectConditions);
  const rowsAreaValues = useSelector(selectRows);
  const columnsAreaValues = useSelector(selectColumns);
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentFieldId, setCurrentFieldId] = useState<string>();

  const currentField = useMemo(() => {
    if (!currentFieldId) {
      return undefined;
    }
    return rowsAreaValues.find((f) => f.config.guid === currentFieldId);
  }, [currentFieldId, rowsAreaValues]);

  const columnsRef = useRef<ReportField[]>([]);
  useEffect(() => {
    columnsRef.current = columnsAreaValues;
  }, [columnsAreaValues]);

  const rowsRef = useRef<ReportField[]>([]);
  useEffect(() => {
    rowsRef.current = rowsAreaValues;
  }, [rowsAreaValues]);

  const showOptions = useCallback((settings: ShowFieldOptionsSettings<ReportField>) => {
    setCurrentFieldId(settings.field.config.guid);
    setAnchorEl(settings.ref);
  }, []);

  const canDropItem = useCallback(
    (item: FieldWrapper<ReportField> | DraggableMetaType) => canDropRowItem(item, rowsRef.current, columnsRef.current),
    []
  );

  const saveChanges = useCallback(
    (changes: Partial<FieldConfiguration>) => {
      if (currentField === undefined) {
        return;
      }
      dispatch(fieldsStateActions.updateRowConfig({ field: currentField, changes }));
    },
    [currentField, dispatch]
  );

  const handleSaveChanges = useCallback(
    (changes: Partial<FieldConfiguration>, causeClosing: boolean) => {
      saveChanges(changes);
      if (causeClosing) {
        setAnchorEl(null);
      }
    },
    [saveChanges]
  );

  const handleOnRemove = useCallback(() => {
    setAnchorEl(null);
    if (currentField !== undefined) {
      dispatch(fieldsStateActions.removeRow(currentField));
    }
  }, [currentField, dispatch]);

  const handleOnCancel = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleRowFieldCustomLabelChange = (name: string | undefined) => {
    if (currentField) {
      dispatch(fieldsStateActions.updateRowConfig({ field: currentField, changes: { customLabel: name } }));
    }
  };

  const addItem = useCallback(
    (item: ReportField, index: number) => {
      dispatch(fieldsStateActions.addRow({ field: item, index }));
    },
    [dispatch]
  );

  const removeItem = useCallback(
    (item: ReportField) => {
      dispatch(fieldsStateActions.removeRow(item));
    },
    [dispatch]
  );

  const moveItem = useCallback(
    (item: ReportField, newIndex: number) => {
      dispatch(fieldsStateActions.moveRow({ field: item, newIndex }));
    },
    [dispatch]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
      <DropFieldContainer
        title="Rows"
        maxFields={MaxRowsNumber}
        areaFieldType={AreaItemType.ROWS}
        acceptedDropTypes={[
          AreaItemType.DIMENSION,
          AreaItemType.COLUMNS,
          AreaItemType.CONDITIONS,
          AreaItemType.VALUES,
          AreaItemType.MEASURE,
        ]}
        fields={rowsAreaValues}
        getKeyValue={getKeyValue}
        formatCaption={formatDimensionFieldCaption}
        canDropItem={canDropItem}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        onMoveItem={moveItem}
        showOptions={showOptions}
        createListItem={createRowField}
        readonly={readonlyMode}
      />
      {anchorEl && isDimension(currentField) && (
        <RowFieldOptionPopup
          anchorEl={anchorEl}
          field={currentField}
          onUpdateConfiguration={handleSaveChanges}
          cancel={handleOnCancel}
          onRemove={handleOnRemove}
          onUpdateCustomLabel={handleRowFieldCustomLabelChange}
          readonly={readonlyMode}
        />
      )}
      {anchorEl && isMeasure(currentField) && currentField.meta.capabilities.isScalar && (
        <FieldOptionPopupContainer anchorEl={anchorEl} cancel={handleOnCancel}>
          <MeasureFieldOptions
            field={currentField}
            dimensionFields={getDimensionFields(rowsAreaValues.concat(columnsAreaValues))}
            conditions={conditionsAreaValues}
            otherOptions={
              <OtherOptionsWrapper>
                <SuppressBlankValues
                  field={currentField}
                  onChanged={saveChanges}
                  showExcludeZeroValues={currentField.config.hideBlankValues === true}
                  readonly={readonlyMode}
                />
              </OtherOptionsWrapper>
            }
            cancel={handleOnCancel}
            saveChanges={handleSaveChanges}
            onRemove={handleOnRemove}
            onCalculationItemClick={undefined}
            canAddCustomField={false}
            readonly={readonlyMode}
          />
        </FieldOptionPopupContainer>
      )}
    </Box>
  );
}

function getKeyValue(field: FieldWrapper<DraggableFieldType> | DraggableMetaType) {
  return extractMeta(field)?.name ?? "";
}
